import * as React from 'react';
import { Link } from 'react-router-dom';
import { Award, Check, Languages, MapPin, Tag, User } from 'lucide-react';

export interface DirectoryEntry {
  id: string;
  display_name: string;
  headline: string;
  industries: string;
  specializations: string;
  years_experience: number | null;
  contact_email: string;
  photo_url: string;
  languages: string;
  country: string;
  cities: string;
}

interface Props {
  advisors: DirectoryEntry[];
  onSelect?: (advisor: DirectoryEntry) => void;
  selectedId?: string | null;
  columns?: 'auto' | 'single';
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function AdvisorAvatar({ advisor, size = 'md' }: { advisor: DirectoryEntry; size?: 'md' | 'lg' }) {
  const box = size === 'lg' ? 'h-20 w-20 text-2xl' : 'h-14 w-14 text-base';
  if (advisor.photo_url) {
    return (
      <img
        src={advisor.photo_url}
        alt={advisor.display_name}
        className={`${box} shrink-0 rounded-full object-cover ring-2 ring-background shadow-sm`}
      />
    );
  }
  const letters = initials(advisor.display_name);
  return (
    <div
      className={`${box} flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-600 font-semibold text-white ring-2 ring-background shadow-sm`}
    >
      {letters || <User className="h-6 w-6" />}
    </div>
  );
}

function Fact({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{children}</span>
    </span>
  );
}

function firstTwo(csv: string) {
  return csv.split(',').map((v) => v.trim()).filter(Boolean).slice(0, 2).join(', ');
}

export function AdvisorCard({ advisor, selected }: { advisor: DirectoryEntry; selected?: boolean }) {
  return (
    <div
      className={`group relative h-full rounded-xl border bg-card p-4 transition-all ${
        selected
          ? 'border-amber-500 bg-amber-50/40 shadow-md ring-1 ring-amber-500/40'
          : 'hover:-translate-y-0.5 hover:border-amber-500/40 hover:shadow-md'
      }`}
    >
      {selected && (
        <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-white">
          <Check className="h-3.5 w-3.5" />
        </span>
      )}
      <div className="flex items-start gap-4">
        <AdvisorAvatar advisor={advisor} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate pr-6 text-base font-semibold leading-tight">{advisor.display_name}</h3>
          {advisor.headline && (
            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{advisor.headline}</p>
          )}
          <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1">
            {advisor.years_experience ? <Fact icon={Award}>{advisor.years_experience} years</Fact> : null}
            {advisor.industries && <Fact icon={Tag}>{firstTwo(advisor.industries)}</Fact>}
            {advisor.country && (
              <Fact icon={MapPin}>{advisor.cities ? `${firstTwo(advisor.cities)}, ${advisor.country}` : advisor.country}</Fact>
            )}
            {advisor.languages && <Fact icon={Languages}>{firstTwo(advisor.languages)}</Fact>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdvisorCardGrid({ advisors, onSelect, selectedId, columns = 'auto' }: Props) {
  const grid = columns === 'single' ? 'grid gap-3' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={grid} data-testid="directory-list">
      {advisors.map((advisor) =>
        onSelect ? (
          <button
            key={advisor.id}
            type="button"
            onClick={() => onSelect(advisor)}
            data-testid={`advisor-card-${advisor.id}`}
            aria-pressed={selectedId === advisor.id}
            className="block rounded-xl text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            <AdvisorCard advisor={advisor} selected={selectedId === advisor.id} />
          </button>
        ) : (
          <Link
            key={advisor.id}
            to={`/public/advisor/${advisor.id}`}
            data-testid={`advisor-card-${advisor.id}`}
            className="block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            <AdvisorCard advisor={advisor} />
          </Link>
        ),
      )}
    </div>
  );
}
